import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { SYSTEMS, BRAND_FILM, HERO_CAREERS } from "@/data/content";
import { useQuiz } from "@/context/QuizContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { VideoLightbox } from "@/components/site/VideoLightbox";
import "./Hero.css";

export const Hero = ({ copy = HERO_CAREERS }) => {
  const PROOF = copy.proof;
  const ticker = copy.ticker || SYSTEMS.map((s) => `${s.name} · ${s.label}`);
  const { openQuiz } = useQuiz();
  const { openEnquiry } = useEnquiry();
  const [playFilm, setPlayFilm] = useState(false);
  const heroRef = useRef(null);
  const photoRef = useRef(null);
  const contentRef = useRef(null);
  const canvasRef = useRef(null);

  // Scroll parallax + pointer parallax + magnetic buttons
  useEffect(() => {
    const hero = heroRef.current;
    const photo = photoRef.current;
    const content = contentRef.current;
    if (!hero) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const onScroll = () => {
      if (reduced) return;
      const y = window.scrollY;
      if (y < hero.offsetHeight) {
        const p = Math.min(1, y / hero.offsetHeight);
        photo && photo.style.setProperty("--ah-photo-y", `${p * 22}px`);
        if (content) {
          content.style.opacity = String(1 - p * 0.55);
          content.style.setProperty("--ah-copy-y", `${p * 34}px`);
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const fine = window.matchMedia("(pointer:fine)").matches;
    let onMove, onLeave;
    const magnets = Array.from(hero.querySelectorAll("[data-magnetic]"));
    const magnetHandlers = [];

    if (!reduced && fine) {
      onMove = (e) => {
        const r = hero.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        hero.style.setProperty("--ah-mx", `${e.clientX - r.left}px`);
        hero.style.setProperty("--ah-my", `${e.clientY - r.top}px`);
        photo && photo.style.setProperty("--ah-photo-x", `${(x - 0.5) * -14}px`);
        photo && photo.style.setProperty("--ah-photo-y", `${(y - 0.5) * -8}px`);
        if (content) {
          content.style.setProperty("--ah-copy-x", `${(x - 0.5) * 7}px`);
          content.style.setProperty("--ah-copy-y", `${(y - 0.5) * 5}px`);
        }
      };
      onLeave = () => {
        photo && (photo.style.setProperty("--ah-photo-x", "0px"), photo.style.setProperty("--ah-photo-y", "0px"));
        if (content) { content.style.setProperty("--ah-copy-x", "0px"); content.style.setProperty("--ah-copy-y", "0px"); }
      };
      hero.addEventListener("pointermove", onMove);
      hero.addEventListener("pointerleave", onLeave);

      magnets.forEach((btn) => {
        const move = (e) => {
          const r = btn.getBoundingClientRect();
          btn.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.09}px, ${(e.clientY - r.top - r.height / 2) * 0.13}px)`;
        };
        const leave = () => { btn.style.transform = ""; };
        btn.addEventListener("pointermove", move);
        btn.addEventListener("pointerleave", leave);
        magnetHandlers.push([btn, move, leave]);
      });
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (onMove) hero.removeEventListener("pointermove", onMove);
      if (onLeave) hero.removeEventListener("pointerleave", onLeave);
      magnetHandlers.forEach(([btn, move, leave]) => {
        btn.removeEventListener("pointermove", move);
        btn.removeEventListener("pointerleave", leave);
      });
    };
  }, []);

  // Interactive particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = heroRef.current;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!canvas || !hero || reduced) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let nodes = [];
    let frame = 0;
    let active = true;
    let pointer = { x: -999, y: -999 };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(72, Math.max(38, Math.floor(rect.width / 26)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * 0.16,
        vy: (Math.random() - 0.5) * 0.13,
        r: Math.random() * 1.25 + 0.45,
      }));
    };

    const draw = () => {
      if (!active) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      nodes.forEach((node, index) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < -8) node.x = w + 8;
        if (node.x > w + 8) node.x = -8;
        if (node.y < -8) node.y = h + 8;
        if (node.y > h + 8) node.y = -8;

        const pd = Math.hypot(node.x - pointer.x, node.y - pointer.y);
        if (pd < 140) {
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(pointer.x, pointer.y);
          ctx.strokeStyle = `rgba(241,214,142,${(1 - pd / 140) * 0.25})`;
          ctx.lineWidth = 0.7;
          ctx.stroke();
        }
        for (let j = index + 1; j < nodes.length; j += 1) {
          const other = nodes[j];
          const d = Math.hypot(node.x - other.x, node.y - other.y);
          if (d < 118) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(212,168,77,${(1 - d / 118) * 0.09})`;
            ctx.lineWidth = 0.55;
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(241,214,142,.46)";
        ctx.fill();
      });
      frame = requestAnimationFrame(draw);
    };

    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
    };
    const onPointerLeave = () => { pointer = { x: -999, y: -999 }; };
    hero.addEventListener("pointermove", onPointerMove);
    hero.addEventListener("pointerleave", onPointerLeave);

    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas);
    const io = new IntersectionObserver((entries) => {
      active = entries[0].isIntersecting;
      cancelAnimationFrame(frame);
      if (active) frame = requestAnimationFrame(draw);
    }, { threshold: 0.03 });
    io.observe(hero);

    resize();
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      hero.removeEventListener("pointermove", onPointerMove);
      hero.removeEventListener("pointerleave", onPointerLeave);
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  const scrollNext = () => {
    const target = heroRef.current?.nextElementSibling;
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={heroRef} data-testid="hero-section" className="ah-hero" aria-labelledby="ah-hero-title">
      <div className="ah-hero__photo-wrap" aria-hidden="true">
        <img ref={photoRef} className="ah-hero__photo" src={copy.photo || "/img/team-group.webp"} style={copy.photoPosition ? { objectPosition: copy.photoPosition } : undefined} alt="" width="1920" height="1080" fetchPriority="high" />
      </div>
      <div className="ah-hero__grade" aria-hidden="true" />
      <div className="ah-hero__spotlight" aria-hidden="true" />
      <div className="ah-hero__grid" aria-hidden="true" />
      <canvas ref={canvasRef} className="ah-hero__canvas" aria-hidden="true" />
      <div className="ah-hero__beam" aria-hidden="true" />
      <div className="ah-hero__noise" aria-hidden="true" />

      <div className="ah-hero__inner">
        <div ref={contentRef} className="ah-hero__content">
          <p className="ah-hero__eyebrow"><i />{copy.eyebrow}</p>
          <h1 className="ah-hero__title" id="ah-hero-title">
            <span><b>{copy.title[0]}</b></span>
            <em><b>{copy.title[1]}</b></em>
            <span className="ah-hero__together"><b>{copy.title[2]}</b></span>
          </h1>
          <p className="ah-hero__lede">
            <strong>{copy.ledeStrong}</strong> {copy.lede}
          </p>
          <div className="ah-hero__actions">
            {copy.primary.quiz ? (
              <button data-testid="hero-fit-check-button" className="ah-hero__primary" type="button" data-magnetic onClick={() => openQuiz("hero")}>
                {copy.primary.label} <span aria-hidden>↗</span>
              </button>
            ) : (
              <button data-testid="hero-primary-link" className="ah-hero__primary" type="button" data-magnetic onClick={() => openEnquiry("advisor")}>
                {copy.primary.label} <span aria-hidden>↗</span>
              </button>
            )}
            <Link data-testid="hero-platform-link" className="ah-hero__secondary" to={copy.secondary.to} data-magnetic>
              {copy.secondary.label} <span aria-hidden>↓</span>
            </Link>
          </div>
          <button type="button" data-testid="hero-watch-film" className="ah-hero__film" onClick={() => setPlayFilm(true)}>
            <span className="ah-hero__film-icon"><Play size={11} fill="currentColor" /></span>
            Watch the film <em>· 60 sec</em>
          </button>
          <p className="ah-hero__microcopy">{copy.microcopy}</p>
        </div>
      </div>

      {copy.signal && (
        <aside className="ah-hero__signal-card" data-testid="hero-signal-card" aria-label="Team highlight">
          <div className="ah-hero__signal-top">
            <span>{copy.signal.milestone}</span>
            <span className="ah-hero__signal-live"><i />LIVE</span>
          </div>
          <strong>{copy.signal.stat} <span>{copy.signal.unit}</span></strong>
          <p>{copy.signal.blurb}</p>
          <div className="ah-hero__signal-track" aria-hidden="true">
            <svg viewBox="0 0 280 38" preserveAspectRatio="none">
              <path d="M2 32 L38 28 L74 30 L110 20 L146 23 L182 12 L218 15 L254 4 L278 6" />
            </svg>
          </div>
        </aside>
      )}

      <div className="ah-hero__proof" aria-label="Affinity highlights">
        {PROOF.map((t, i) => (
          <span key={t} className="ah-hero__proof-item"><b>0{i + 1}</b><span>{t}</span></span>
        ))}
        <button type="button" className="ah-hero__scroll" data-testid="hero-scroll-cue" onClick={scrollNext}>
          <i /><span>{copy.signal ? "Scroll to explore" : "Choose your starting point"}</span>
        </button>
      </div>

      <div className="ah-hero__ticker" aria-hidden="true">
        <div className="ah-hero__ticker-track">
          <div className="ah-hero__ticker-group">
            {ticker.map((s) => <span key={s}>{s}</span>)}
          </div>
          <div className="ah-hero__ticker-group">
            {ticker.map((s) => <span key={`${s}-2`}>{s}</span>)}
          </div>
        </div>
      </div>

      <VideoLightbox video={playFilm ? BRAND_FILM : null} title={BRAND_FILM.title} kicker="Affinity brand film" onClose={() => setPlayFilm(false)} />
    </section>
  );
};
